/**
 * <collection-listing> - behavior for the collection listing section.
 *
 * Port of live's product-listing behavior (the ProductListing custom
 * element in the legacy bundle plus the section's inline show-more script), scoped
 * to the section element instead of document-wide listeners.
 *
 * What it does, exactly as live:
 * - "Show more" REVEALS already-rendered grid items batch by batch (there is
 *   no ajax fetch anywhere in collection loading); once everything is shown
 *   the button becomes "show less" and collapses back to the initial count.
 * - Optional auto-load: an IntersectionObserver clicks the button when it
 *   scrolls into view, armed only after the first real scroll, with live's
 *   300ms loader pulse between batches.
 * - Re-runs the REVIEWS.io rich-snippet script after each reveal (live
 *   behavior; stars are also server-rendered, so this only refreshes widgets).
 * - Nav scrollspy for NON-link nav items (multi-collection layouts): observes
 *   the collection titles, highlights and centers the matching nav item, and
 *   smooth-scrolls to a title on click. Link-type nav items (the default
 *   template) navigate natively and are excluded, as on live.
 *
 * Taste-profile filter + sort (D-17 item 8, new - convention and models
 * documented in sections/collection-listing.liquid):
 * - Pills filter the server-rendered cards by their data-taste tokens via a
 *   CSS-reveal class (the home video-grid pattern); 'Alle' resets; a
 *   visually-hidden status line announces the visible count.
 * - 'Nach Geschmacksrichtung' sort GROUPS the grid by pill order with a
 *   stable DOM reorder (visual, tab and reader order stay identical; the
 *   D-02 purchasable-first ordering is preserved inside each group);
 *   'Standard' restores the server order verbatim.
 * - While filter or sort is active all matching cards are revealed and the
 *   show-more control hides (CSS); returning to 'Alle' + 'Standard'
 *   restores the collapsed show-more state.
 */
class CollectionListing extends HTMLElement {
  connectedCallback() {
    this.navItems = this.querySelectorAll('.collection-listing-nav-item:not(.collection-nav-item)');
    this.collectionTitles = this.querySelectorAll('.collection-listing-collection-title');
    this.nav = this.querySelector('.collection-listing-nav');

    this.#setupShowMore();
    this.#setupTasteFilter();
    if (this.navItems.length > 0 && this.nav) {
      this.navItems.forEach((item) => item.addEventListener('click', (event) => this.#onNavItemClick(event)));
      this.#setupScrollSpy();
      this.#setupScrollHandler();
    }
  }

  /* ------------------------------------------------ show more / show less */

  #setupShowMore() {
    this.querySelectorAll('.show-btn-button').forEach((button) => {
      const container = button.closest('.show-btn-container');
      const grid = this.querySelector(`#${CSS.escape(button.getAttribute('data-target'))}`);
      if (!grid || !container) return;

      const initialCount = parseInt(grid.getAttribute('data-initial-count'), 10) || 8;
      const batchSize = parseInt(grid.getAttribute('data-products-per-show-more'), 10) || 8;
      const moreLabel = container.getAttribute('data-show-more-label') || 'Mehr anzeigen';
      const lessLabel = container.getAttribute('data-show-less-label') || 'Weniger anzeigen';

      const showMore = () => {
        const hidden = Array.from(grid.querySelectorAll('.hidden-product'));
        if (hidden.length > 0) {
          hidden.slice(0, batchSize).forEach((item) => item.classList.remove('hidden-product'));
          this.#reinitReviewsWidget();
          if (grid.querySelectorAll('.hidden-product').length === 0) {
            button.textContent = lessLabel;
            return false;
          }
          return true;
        }
        const allItems = Array.from(grid.querySelectorAll('.grid__item:not(.grid__item-extra)'));
        allItems.forEach((item, index) => {
          if (index >= initialCount) item.classList.add('hidden-product');
        });
        button.textContent = moreLabel;
        button.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return false;
      };

      button.addEventListener('click', showMore);

      if (container.getAttribute('data-auto-load') === 'true' && 'IntersectionObserver' in window) {
        const loader = container.querySelector('.show-btn-loader');
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              observer.unobserve(container);
              if (loader) loader.style.display = 'flex';
              button.classList.add('loading');
              setTimeout(() => {
                const hasMore = showMore();
                if (loader) loader.style.display = 'none';
                button.classList.remove('loading');
                if (hasMore) {
                  setTimeout(() => observer.observe(container), 300);
                }
              }, 300);
            });
          },
          { rootMargin: '100px', threshold: 0.1 }
        );
        window.addEventListener(
          'scroll',
          () => {
            observer.observe(container);
          },
          { once: true }
        );
      }
    });
  }

  /* --------------------------------------------- taste filter + sort */

  #setupTasteFilter() {
    this.querySelectorAll('[data-taste-filter]').forEach((bar) => {
      const grid = this.querySelector(`#${CSS.escape(bar.getAttribute('data-target'))}`);
      if (!grid) return;

      const pills = Array.from(bar.querySelectorAll('.taste-filter__pill'));
      const sortSelect = bar.querySelector('[data-taste-sort]');
      const status = bar.querySelector('[data-taste-status]');
      const countTemplate = bar.getAttribute('data-count-template') || '[count]';
      const pillOrder = pills.map((pill) => pill.dataset.tasteValue).filter((value) => value !== '');
      const items = Array.from(grid.querySelectorAll('.grid__item:not(.grid__item-extra)'));
      const extraItem = grid.querySelector('.grid__item-extra');

      let activeValue = '';
      let activeSort = 'standard';

      const tasteOf = (item) => (item.getAttribute('data-taste') || '').split(' ').filter(Boolean);

      const groupRank = (item) => {
        const list = tasteOf(item);
        for (let index = 0; index < pillOrder.length; index += 1) {
          if (list.includes(pillOrder[index])) return index;
        }
        return pillOrder.length;
      };

      const apply = () => {
        const enhanced = activeValue !== '' || activeSort === 'taste';
        grid.classList.toggle('taste-active', enhanced);

        // Order: grouped by pill order when sorting (Array.prototype.sort is
        // stable, so the server order - purchasable before sold-out - is kept
        // inside each group); the untouched `items` array IS the server order.
        const ordered =
          activeSort === 'taste' ? items.slice().sort((a, b) => groupRank(a) - groupRank(b)) : items;
        ordered.forEach((item) => grid.appendChild(item));
        if (extraItem) grid.appendChild(extraItem);

        items.forEach((item) => {
          const match = activeValue === '' || tasteOf(item).includes(activeValue);
          item.classList.toggle('taste-hidden', !match);
          if (enhanced) item.classList.remove('hidden-product');
        });

        if (!enhanced) this.#collapseShowMore(grid, items);

        const shown = items.filter(
          (item) => !item.classList.contains('taste-hidden') && !item.classList.contains('hidden-product')
        ).length;
        if (status) status.textContent = countTemplate.replace('[count]', String(shown));
      };

      pills.forEach((pill) => {
        pill.addEventListener('click', () => {
          activeValue = pill.dataset.tasteValue;
          pills.forEach((candidate) => {
            const isActive = candidate === pill;
            candidate.classList.toggle('active', isActive);
            candidate.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });
          apply();
        });
      });

      if (sortSelect) {
        sortSelect.addEventListener('change', () => {
          activeSort = sortSelect.value;
          apply();
        });
      }
    });
  }

  /* Restore the show-more collapsed state (items past the initial count
     hidden, button back on its "more" label) after the taste enhancement
     revealed everything. No-op on paginated grids (no show-more mode). */
  #collapseShowMore(grid, items) {
    if (grid.getAttribute('data-show-more-enabled') !== 'true') return;
    const initialCount = parseInt(grid.getAttribute('data-initial-count'), 10) || 8;
    items.forEach((item, index) => item.classList.toggle('hidden-product', index >= initialCount));
    const button = Array.from(this.querySelectorAll('.show-btn-button')).find(
      (candidate) => candidate.getAttribute('data-target') === grid.id
    );
    if (!button) return;
    const container = button.closest('.show-btn-container');
    button.textContent = (container && container.getAttribute('data-show-more-label')) || 'Mehr anzeigen';
  }

  #reinitReviewsWidget() {
    document.querySelectorAll('script[src*="reviews.io"]').forEach((script) => {
      if (!script.src) return;
      const separator = script.src.indexOf('?') > -1 ? '&' : '?';
      const clone = document.createElement('script');
      clone.src = `${script.src.split('#')[0]}${separator}_rio_rerun=${Date.now()}`;
      if (script.async) clone.async = true;
      if (script.defer) clone.defer = true;
      document.head.appendChild(clone);
    });
  }

  /* ----------------------------------------------------- nav scroll spy */

  #onNavItemClick(event) {
    this.navItems.forEach((item) => item.classList.remove('active-nav-item'));
    const navTitle = event.target.dataset.navTitle;
    const target = Array.from(this.collectionTitles).find(
      (element) => element.dataset.collectionTitle === navTitle
    );
    if (target) {
      const offset = window.innerWidth < 750 ? 110 : 70;
      const top = target.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: top - offset, behavior: 'smooth' });
    }
    setTimeout(() => this.#scrollNavItemIntoView(event.target), 500);
  }

  #setupScrollSpy() {
    const offset = window.innerWidth < 750 ? 120 : 80;
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const collectionTitle = entry.target.dataset.collectionTitle;
          this.navItems.forEach((item) => {
            item.classList.toggle('active-nav-item', item.dataset.navTitle === collectionTitle);
          });
        });
      },
      {
        root: null,
        rootMargin: `${offset}px 0px -${window.innerHeight - offset - 1}px 0px`,
        threshold: 0,
      }
    );
    this.collectionTitles.forEach((title) => this.observer.observe(title));
  }

  #setupScrollHandler() {
    let scrollDebounce;
    window.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(scrollDebounce);
        scrollDebounce = setTimeout(() => {
          const activeItem = this.querySelector('.active-nav-item');
          if (activeItem) this.#scrollNavItemIntoView(activeItem);
        }, 200);
      },
      false
    );
  }

  #scrollNavItemIntoView(item) {
    const container = this.nav;
    if (!container) return;
    const desired = item.offsetLeft - container.clientWidth / 2 + item.offsetWidth / 2;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    container.scrollTo({
      left: Math.max(0, Math.min(desired, maxScrollLeft)),
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('collection-listing')) {
  customElements.define('collection-listing', CollectionListing);
}
